import { Link } from 'react-router-dom';
import { Printer, HelpCircle, FileText, MapPin, ExternalLink, ShieldCheck } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="w-full bg-slate-50 text-slate-700 border-t border-slate-200 mt-auto pt-12 pb-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 pb-10 border-b border-slate-200">
        
        {/* Brand Column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2.5">
            <img 
              src="/inchpaper-printezy.png" 
              alt="Inchpaper Print Logo" 
              className="h-10 sm:h-12 object-contain"
              onError={(e) => { e.target.src = '/inchpaper-logo.png'; }}
            />
          </div>

          <p className="text-xs text-slate-600 leading-relaxed max-w-sm">
            India&apos;s Print-on-Demand Platform. Print PDFs, Word Files, Photos, PPTs, Excel Sheets, and Project Reports in 10 Seconds With Instant Online Payment.
          </p>

          <div className="flex items-center gap-2 text-xs text-slate-600 pt-1">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>256-bit Encrypted SSL File Transmission</span>
          </div>
        </div>

        {/* Top Print Services */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">Popular Services</h3>
          <ul className="space-y-2 text-xs">
            <li>
              <Link to="/print-pdf-online" className="hover:text-red-700 transition-colors">Print PDF Online</Link>
            </li>
            <li>
              <Link to="/online-xerox" className="hover:text-red-700 transition-colors">Online Xerox Photocopies</Link>
            </li>
            <li>
              <Link to="/black-and-white-printing" className="hover:text-red-700 transition-colors">Black & White Printing</Link>
            </li>
            <li>
              <Link to="/color-printing" className="hover:text-red-700 transition-colors">Full Color Printing</Link>
            </li>
            <li>
              <Link to="/spiral-binding" className="hover:text-red-700 transition-colors">Spiral & Comb Binding</Link>
            </li>
            <li>
              <Link to="/thesis-printing" className="hover:text-red-700 transition-colors">Hardbound Thesis Print</Link>
            </li>
          </ul>
        </div>

        {/* Specialized Document Print */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">Document Solutions</h3>
          <ul className="space-y-2 text-xs">
            <li>
              <Link to="/project-report-printing" className="hover:text-red-700 transition-colors">Project Report Printing</Link>
            </li>
            <li>
              <Link to="/resume-printing" className="hover:text-red-700 transition-colors">Resume & CV Printing</Link>
            </li>
            <li>
              <Link to="/certificate-printing" className="hover:text-red-700 transition-colors">Certificate & Award Printing</Link>
            </li>
            <li>
              <Link to="/poster-printing" className="hover:text-red-700 transition-colors">Poster & Banner Print</Link>
            </li>
            <li>
              <Link to="/photo-printing" className="hover:text-red-700 transition-colors">Glossy Photo Printing</Link>
            </li>
            <li>
              <Link to="/express-printing" className="hover:text-red-700 transition-colors">Express Kiosk Print</Link>
            </li>
          </ul>
        </div>

        {/* Resources & Locations */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources & Info</h3>
          <ul className="space-y-2 text-xs">
            <li>
              <Link to="/printing-services" className="hover:text-red-700 transition-colors">Printing Services</Link>
            </li>
            <li>
              <Link to="/printing-prices" className="hover:text-red-700 transition-colors">Printing Rates & Pricing</Link>
            </li>
            <li>
              <Link to="/print-centers" className="hover:text-red-700 transition-colors flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-500" />
                <span>Print Centers</span>
              </Link>
            </li>
            <li>
              <Link to="/blog" className="hover:text-red-700 transition-colors flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-slate-500" />
                <span>Blog</span>
              </Link>
            </li>
            <li>
              <Link to="/faq" className="hover:text-red-700 transition-colors flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5 text-slate-500" />
                <span>Help & FAQs</span>
              </Link>
            </li>
            <li>
              <Link to="/contact-us" className="hover:text-red-700 transition-colors font-medium text-[#7d0909]">
                Contact Us
              </Link>
            </li>
            <li>
              <a 
                href="https://inchpaper.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-red-700 transition-colors flex items-center gap-1 text-slate-600 pt-1"
              >
                <span>Main Website (inchpaper.com)</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
          </ul>
        </div>

      </div>

      {/* Copyright & Legal */}
      <div className="max-w-7xl mx-auto pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
        <p>© 2026, Inchpaper Private Limited. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <Link to="/#all-services" className="hover:text-slate-800">Sitemap Services</Link>
          <a href="/sitemap.xml" target="_blank" className="hover:text-slate-800">XML Sitemap</a>
          <a href="/robots.txt" target="_blank" className="hover:text-slate-800">Robots.txt</a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
